import { Directive, ElementRef, Input, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { DetectionAnimationService, AnimationState } from '@services/detection-animation-service/detection-animation.service';

@Directive({
  selector: '[appDrawDetections]',
  standalone: false,
})
export class DrawDetectionsDirective implements OnDestroy {
  @Input() appDrawDetections: any;
  @Input() config: { top: number, left: number, ratio: number, offsetX?: number, offsetY?: number };
  canvas: HTMLCanvasElement;
  canvasContext: CanvasRenderingContext2D;

  scale: number = 1;
  lineDashOffset: number = 0;
  private animationSubscription: Subscription | null = null;
  private isInitialized: boolean = false;

  constructor(
    elementRef: ElementRef<HTMLCanvasElement>,
    private animationService: DetectionAnimationService
  ) {
    this.canvas = elementRef.nativeElement;
    this.canvasContext = elementRef.nativeElement.getContext('2d');
  }
  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (!this.appDrawDetections || !this.config) {
      this.cleanup();
      return;
    }
    // Ensure canvas dimensions are set
    if (!this.canvas) {
      this.initializeAndDraw();
      return;
    }

    requestAnimationFrame((): void => {
      setTimeout((): void => {
        if (this.canvas.clientWidth > 0 && this.canvas.clientHeight > 0) {
          this.canvas.width = this.canvas.clientWidth;
          this.canvas.height = this.canvas.clientHeight;
          this.initializeAndDraw();
        }
      }, 50);
    });
  }

  private initializeAndDraw(): void {
    if (!this.isInitialized) {
      this.setupAnimationSubscription();
      this.isInitialized = true;
    }
    this.drawDetections();
  }

  private setupAnimationSubscription(): void {
    // Get initial state
    const initialState = this.animationService.getCurrentState();
    this.scale = initialState.scale;
    this.lineDashOffset = initialState.lineDashOffset;

    // Subscribe to animation updates
    this.animationSubscription = this.animationService.getAnimationState().subscribe((state: AnimationState): void => {
      this.scale = state.scale;
      this.lineDashOffset = state.lineDashOffset;
      this.animate();
    });

    // Start the shared animation loop
    this.animationService.startAnimation();
  }

  private drawDetections(): void {
    if (this.appDrawDetections.BoundingBoxes) {
      this.drawCircles(this.appDrawDetections.BoundingBoxes);
    }
    if (this.appDrawDetections.PolygonPointsSequences) {
      this.drawPolygons(this.appDrawDetections.PolygonPointsSequences);
    }
  }

  private animate(): void {
    if (!this.canvas || !this.canvasContext) {
      return;
    }
    this.canvasContext.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.drawDetections();
  }

  private cleanup(): void {
    if (this.animationSubscription) {
      this.animationSubscription.unsubscribe();
      this.animationSubscription = null;
    }
    this.animationService.stopAnimation();
    this.isInitialized = false;
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  drawCircles(circles: string[]): void {
    circles.forEach((pointString: string, index: number): void => {
      const point: number[] = pointString.split(',').map((x: string): number => +x);

      const centerX = (((point[0] + point[2]) / 2 - this.config.left) * this.config.ratio) + (this.config.offsetX || 0);
      const centerY = (((point[1] + point[3]) / 2 - this.config.top) * this.config.ratio) + (this.config.offsetY || 0);
      const radius = (Math.abs(point[0] - point[2]) * this.config.ratio) / 2;
      const scaledX = centerX - (centerX * this.scale);
      const scaledY = centerY - (centerY * this.scale);
      this.canvasContext.beginPath();
      this.canvasContext.setTransform(this.scale, 0, 0, this.scale, scaledX, scaledY);
      this.canvasContext.arc(centerX, centerY, radius, 0, 2 * Math.PI);
      this.canvasContext.setTransform(1, 0, 0, 1, 0, 0);
      this.canvasContext.lineWidth = 2;
      this.canvasContext.strokeStyle = 'rgba(237, 4, 0)';
      this.canvasContext.stroke();
      this.canvasContext.closePath();
    });
  }

  drawPolygons(polygons: (string[] | { points: string[], color: string })[]): void {
    polygons.forEach((polygon: (string[] | { points: string[], color: string })): void => {
      this.canvasContext.beginPath();
      this.canvasContext.setLineDash([7, 3, 7, 5]);
      this.canvasContext.lineDashOffset = this.lineDashOffset;
      const points = ('points' in polygon) ? polygon.points : polygon;
      points.forEach((pointString: string | number[], index: number): void => {
        const point = typeof pointString === 'string'
          ? pointString.split(',').map((x: string): number => +x)
          : pointString;
        const pointX = ((point[0] - this.config.left) * this.config.ratio) + (this.config.offsetX || 0);
        const pointY = ((point[1] - this.config.top) * this.config.ratio) + (this.config.offsetY || 0);
        if (index === 0) this.canvasContext.moveTo(pointX, pointY);
        else this.canvasContext.lineTo(pointX, pointY);
      });
      this.canvasContext.lineWidth = 1.5;
      this.canvasContext.strokeStyle = ('color' in polygon) ? polygon.color : 'rgb(237, 4, 0)';
      this.canvasContext.stroke();
      this.canvasContext.closePath();
    });
  }
}
